import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { authAPI } from "../services/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("reclaim_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // ── App-wide 401 interceptor ──────────────────────────────────────
  // Any expired/invalid token response across the entire app silently
  // clears the local session and redirects to /login. We skip the
  // auth endpoints themselves so the login form can surface its own
  // "wrong password" message without an instant bounce.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        const isAuthEndpoint = /\/auth\/(login|register|forgot-password)/.test(url);
        if ((status === 401 || (status === 403 && /token/i.test(err?.response?.data?.detail || ""))) && !isAuthEndpoint) {
          try {
            localStorage.removeItem("reclaim_token");
            localStorage.removeItem("token");
            delete axios.defaults.headers.common["Authorization"];
          } catch (_) { /* noop */ }
          // Only bounce if we're not already on the login page so we
          // don't fight an in-progress login attempt.
          if (typeof window !== "undefined" && !/\/login/.test(window.location.pathname)) {
            // Preserve the user's intended destination for post-login
            // redirect. Login page reads ?redirect= (existing pattern).
            const next = window.location.pathname + window.location.search;
            window.location.href = `/login?redirect=${encodeURIComponent(next)}`;
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Whenever we get a fresh user object, pin their email as the canonical
  // user_id used by Vision Board / Living Creation flows. Also fire a one-time
  // claim so any visions saved while anonymous get re-keyed to this email.
  useEffect(() => {
    if (!user?.email) return;
    const previous = localStorage.getItem("userId");
    const claimed = localStorage.getItem("reclaim_claimed_for");
    localStorage.setItem("userId", user.email);
    if (
      previous &&
      previous !== user.email &&
      previous.startsWith("user_") &&
      claimed !== user.email
    ) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/vision-board/claim`;
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ anon_user_id: previous, claim_user_id: user.email }),
      })
        .then(() => localStorage.setItem("reclaim_claimed_for", user.email))
        .catch(() => {});
    } else {
      localStorage.setItem("reclaim_claimed_for", user.email);
    }
  }, [user?.email]);

  // Only a real 401/403 signs you out; server or network blips retry and keep the session.
  const fetchUser = async () => {
    for (const wait of [0, 1000, 2500, 5000]) {
      if (wait) await new Promise((r) => setTimeout(r, wait));
      try {
        const res = await authAPI.getMe();
        setUser(res.data);
        localStorage.setItem("reclaim_user_cache", JSON.stringify(res.data));
        setLoading(false);
        return;
      } catch (e) {
        const s = e?.response?.status;
        if (s === 401 || s === 403) { logout(); setLoading(false); return; }
      }
    }
    try { setUser(JSON.parse(localStorage.getItem("reclaim_user_cache"))); } catch { /* none cached */ }
    setLoading(false);
  };

  const login = async (email, password) => {
    const res = await authAPI.login(email, password);
    localStorage.setItem("reclaim_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, referralCode = null) => {
    const res = await authAPI.register(name, email, password, referralCode);
    localStorage.setItem("reclaim_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("reclaim_token");
    localStorage.removeItem("reclaim_user_cache");
    localStorage.removeItem("userId");
    localStorage.removeItem("reclaim_claimed_for");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const res = await authAPI.updateProfile(data);
    setUser(res.data);
  };

  const refreshUser = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateProfile, fetchUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
